import { ChangeDetectionStrategy, Component, inject, OnInit, computed } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { PhoneNumberDirective } from 'src/app/shared/directives/phone-number.directive';
import { ButtonComponent, FormFieldComponent, FormGroupComponent, FormRowComponent, IconComponent, InputDirective, LabelComponent, NotificationService, OptionComponent, PlaceholderComponent, SelectComponent, SubHeaderComponent } from '@pansa/ui';
import { KeycloakInfoService } from '@app/shared/services/keycloak-info.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { UserDTO } from '../../models/user.model';
import { CommonModule } from '@angular/common';
import { FieldErrorComponent } from '@app/shared/components/field-error/field-error.component';

@Component({
  selector: 'rqa-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PhoneNumberDirective,
    SubHeaderComponent,
    ButtonComponent,
    SelectComponent,
    OptionComponent,
    FormRowComponent,
    FormGroupComponent,
    FormFieldComponent,
    LabelComponent,
    InputDirective,
    PlaceholderComponent,
    IconComponent,
    CommonModule,
    FieldErrorComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddUserComponent implements OnInit {
  private keycloakInfoService = inject(KeycloakInfoService);
  private fb = inject(UntypedFormBuilder);
  private userService = inject(UserService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private notificationService = inject(NotificationService);

  phone = new UntypedFormArray([]);
  userForm = this.fb.group({});
  roles = this.keycloakInfoService.roles;
  groups = this.keycloakInfoService.groups;
  time = this.keycloakInfoService.time;
  locale = this.keycloakInfoService.locale;
  editMode: boolean = false;
  isReady = computed(() => this.keycloakInfoService.isDataLoaded());


  ngOnInit(): void {
    this.buildForm();
    const user = this.route.snapshot.data['user'];
    if (user) {
      this.setFormValues(user);
      this.editMode = true;
    }
    this.phone = this.userForm.get('phone') as UntypedFormArray;

  }

  onSubmit() {
    this.userForm.markAllAsTouched();
    if (this.userForm.invalid) {
      return;
    }
    if (this.editMode) {
      this.editUser();
    } else {
      this.addUser();
    }
  }

  addPhone() {
    if (this.phone.controls.length <= 5) {
      this.phone.push(
        this.fb.control('', [
          Validators.required,
          Validators.pattern('^[+]?[0-9 ]{7,19}$'),
          Validators.minLength(7),
          Validators.maxLength(19)
        ])
      );
    }
  }

  removePhone(phoneValue: number) {
    this.phone.removeAt(phoneValue);
  }

  private editUser() {
    const value = this.userForm.getRawValue() as UserDTO;

    this.userService.updateUser(value).subscribe({
      next: (response) => {
        if (response) {
          this.onGoBack();
          this.notificationService.success({ title: 'Sukces', subTitle: 'Pomyślnie edytowano użytkownika' })
          this.userService.refreshUsers();
        }
      },
      error: (error) => {
        if (error.error) {
          console.log(error.error);
        }
      }
    });
  }

  private addUser() {
    const value = this.userForm.getRawValue() as UserDTO;

    this.userService.addUser(value).subscribe({
      next: (response) => {
        this.onGoBack();
        this.notificationService.success({ title: 'Sukces', subTitle: `Dodano użytkownika ${value.username}` });
        this.userService.refreshUsers();
      },
      error: (error) => {
        if (error.error) {
          console.log(error.error);
          this.notificationService.danger({ title: 'Błąd', subTitle: 'Wystąpił błąd podczas dodawania użytkownika' });
        }
      }
    });
  }

  inputUsername() {
    const value = this.userForm.controls.username.value.toLowerCase();
    this.userForm.controls.username.setValue(value);
  }

  inputResponsibleUnit() {
    const value = this.userForm.controls.responsibleUnit.value.toUpperCase();
    this.userForm.controls.responsibleUnit.setValue(value);
  }

  onGoBack() {
    this.router.navigate(['users']);
  }

  private buildForm() {
    this.userForm = this.fb.group({
      username: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(20),
          Validators.pattern('^[a-z][a-zA-Z0-9._-]{8,20}$')
        ]
      ],
      firstName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20),
          Validators.pattern("^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ - . ']{2,20}$")
        ]
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20),
          Validators.pattern("^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ - . ']{2,20}$")
        ]
      ],
      email: ['', [Validators.required, Validators.email]],
      group: [this.groups()[0], [Validators.required]],
      role: [this.roles()[0], [Validators.required]],
      organization: [
        '',
        [Validators.maxLength(50), Validators.pattern("^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ0-9 - . ']{0,50}$")]
      ],
      responsibleUnit: [
        '',
        [
          Validators.required,
          Validators.maxLength(8),
          Validators.minLength(2),
          Validators.pattern('^[0-9]*[A-Z]{2,8}$')
        ]
      ],
      time: [this.time()[0], [Validators.required]],
      locale: [this.locale()[0], [Validators.required]],
      observation: ['', [Validators.maxLength(15)]],
      phone: this.fb.array([
        this.fb.control('', [
          Validators.required,
          Validators.pattern('^[+]?[0-9 ]{7,19}$'),
          Validators.minLength(7),
          Validators.maxLength(19)
        ])
      ])
    });

  }

  private setFormValues(user: UserDTO) {
    this.userForm = this.fb.group({
      username: [
        // disabled in edit mode
        { value: user.username, disabled: true },
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(20),
          Validators.pattern('^[a-z][a-zA-Z0-9._-]{8,20}$')
        ]
      ],
      firstName: [
        user.firstName,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20),
          Validators.pattern("^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ - . ']{2,20}$")
        ]
      ],
      lastName: [
        user.lastName,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20),
          Validators.pattern("^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ -. ']{2,20}$")
        ]
      ],
      email: [user.email, [Validators.required, Validators.email]],
      group: [user.group, [Validators.required]],
      role: [user.role, [Validators.required]],
      organization: [
        user.organization,
        [Validators.maxLength(50), Validators.pattern("^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ0-9 - . ']{0,50}$")]
      ],
      responsibleUnit: [
        user.responsibleUnit,
        [
          Validators.required,
          Validators.maxLength(8),
          Validators.minLength(2),
          Validators.pattern('^[0-9]*[A-Z]{2,8}$')
        ]
      ],
      time: [user.time, [Validators.required]],
      locale: [user.locale.toLowerCase(), [Validators.required]],
      observation: [user.observation, [Validators.maxLength(15), Validators.pattern('^[a-zA-Z0-9]*$')]],
      phone: this.fb.array([])
    });
    this.phone = this.userForm.get('phone') as UntypedFormArray;
    user.phone.forEach((item) => {
      this.phone.push(
        this.fb.control(item, [
          Validators.required,
          Validators.pattern('^[+]?[0-9 ]{7,19}$'),
          Validators.minLength(7),
          Validators.maxLength(19)
        ])
      );
    });
  }
}
